import { useCallback, useEffect, useRef } from "react";

type UseInfiniteScrollProps = {
  isLoading: boolean;
  hasNextPage: boolean | undefined;
  fetchNextPage: () => unknown;
  rootMargin?: string;
};

/**
 * Observes a list sentinel and requests the next page shortly before it enters
 * the viewport. Reconnecting when request state changes prevents duplicate
 * page requests while a fetch is already in flight.
 */
export const useInfiniteScroll = ({
  isLoading,
  hasNextPage,
  fetchNextPage,
  rootMargin = "400px"
}: UseInfiniteScrollProps) => {
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => () => observer.current?.disconnect(), []);

  const lastElementRef = useCallback(
    (node: HTMLElement | null) => {
      observer.current?.disconnect();

      if (!node || isLoading || !hasNextPage) return;

      observer.current = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) void fetchNextPage();
        },
        { rootMargin }
      );
      observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isLoading, rootMargin]
  );

  return { lastElementRef };
};
